/**
 * URL helpers + the "where should we read data from?" decision.
 *
 * Split out of the main index so the routing logic between local
 * fixtures, the preview server, and the live S3 feed is easy to reason
 * about in isolation.
 */

import { IS_PREVIEW_BUILD, S3_URL } from './constants';

import { appState } from '$lib/stores/index.svelte';
import { asset } from '$app/paths';
import { dev } from '$app/environment';
import { isReutersDotcom } from '$utils/env';
import settings from '$lib/config/settings.json';

// Build-time dev/data flags from `src/lib/config/settings.json`.
// These never change while the app is running — the data-toolkit CLI
// edits the JSON file and the change picks up on the next reload.
const USE_LOCAL_DATA = settings.dev.useLocalData;
/** @type {'current' | 'test'} */
const LOCAL_DATA_FOLDER = settings.dev.localDataFolder;

/**
 * Determines whether to use local data based on environment and configuration.
 * - In production (reuters.com): Always use S3 data
 * - In development & on the graphics.thomsonreuters.com preview server:
 *   Respect the `useLocalData` store flag.
 *
 * @returns {boolean} True if should use local data
 */
export function shouldUseLocalData() {
  // On the live Reuters.com domain (production), always use S3 data.
  if (appState.currentUrl && isReutersDotcom(appState.currentUrl)) {
    return false;
  }

  return USE_LOCAL_DATA && (dev || IS_PREVIEW_BUILD);
}

/**
 * Resolves an absolute path under `src/statics/` to the URL to fetch,
 * applying SvelteKit's configured assets base.
 *
 * `path` must start with `/` — `asset()` uses `resolve()` internally and
 * rejects non-absolute inputs.
 *
 * @param {string} path - Absolute path, e.g. `/data/matches.json`.
 * @returns {string}
 */
function resolveLocalAsset(path) {
  // Preview-build SSR uses root-relative URLs (no assets prefix).
  if (IS_PREVIEW_BUILD && typeof window === 'undefined') return path;
  return asset(path);
}

/**
 * Returns the URL for local or S3 endpoint based on app state and environment.
 *
 * @param {string} fn - The filename.
 * @returns {string} The URL for the filename.
 */
export function getUrl(fn) {
  if (shouldUseLocalData()) {
    // Use local data from the configured folder (`current` or `test`)
    return resolveLocalAsset(`/data/${LOCAL_DATA_FOLDER}/${fn}`);
  }
  // Otherwise hit S3 — always against the current tournament year.
  return `${S3_URL}/${fn}`;
}

/**
 * Returns a fallback URL for a local default file in `src/statics/data/fallback/`.
 * Used as a last-resort default when no year-specific or remote file is
 * available, to keep the app functional even with no real data published.
 *
 * @param {string} fn - The filename.
 * @returns {string} The local fallback URL for the filename.
 */
export function getFallbackUrl(fn) {
  return resolveLocalAsset(`/data/fallback/${fn}`);
}
